import React from 'react';
import Container from '@material-ui/core/Container';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import GET_ORGANIZATIONS_INCLUDING_DISTRICT from 'graph/organization/get-organization-including-districts-query';
import PageContainer from '../../@layout/PageContainer';
import { ModuleProps } from '../../../types';
import HeaderBar from '../HeaderBar';
import Grid from '@material-ui/core/Grid';
import OrganizationTile, { OrganizationTypeMap } from './components/OrganizationTile';
import {
  OrganizationType,
  Organization,
  User,
  UserOrganization,
  OrganizationRole,
  OrganizationRoleClassification
} from 'types/schema-generated-types';
import cx from 'clsx';
const useStyles = makeStyles((theme: Theme) => {
  return {
    container: { margin: theme.spacing(2.5) },
    grid: {
      margin: `${theme.spacing(1)}px 0`,
      '&:first-child': {
        marginTop: 0,
        marginBottom: 0
      },
      '&:last-child': {
        marginTop: 0,
        marginBottom: 0
      }
    },
    twoGrids: {
      '&:last-child': {
        marginTop: theme.spacing(1),
        marginBottom: 0
      }
    }
  };
});

const AdminAdminOrganizations: React.FC<ModuleProps> = props => {
  const classes = useStyles();
  const { data, error, loading } = useQuery(GET_ORGANIZATIONS_INCLUDING_DISTRICT);

  const filterSchools = ({ type }: Organization): boolean => type === OrganizationType.SCHOOL;
  const districts =
    data?.organizations.filter(({ type }: Organization) => type === OrganizationType.DISTRICT) ||
    [];
  const schools = data?.organizations.filter(filterSchools) || [];
  const userGroups =
    data?.organizations.filter(({ type }: Organization) => type === OrganizationType.USER_GROUP) ||
    [];

  const twoTypes = React.useMemo(() => {
    let count = 0;
    if (districts.length) {
      count += 1;
    }
    if (schools.length) {
      count += 1;
    }
    if (userGroups.length) {
      count += 1;
    }
    return count === 2;
  }, [districts, schools, userGroups]);

  const filterAdmins = ({ organizationRoles }: UserOrganization): boolean =>
    Boolean(
      organizationRoles.find(
        (role: OrganizationRole) => role.classification === OrganizationRoleClassification.ADMIN
      )
    );
  const getAdmin = ({ user }: UserOrganization): User => user;
  const renderDistricts = React.useCallback((): Array<React.ReactElement> => {
    return districts.map(function DistrictTile(district: Organization) {
      const schools =
        (district?.subOrganizations &&
          (district?.subOrganizations as Array<Organization>).filter(filterSchools)) ||
        [];
      const admins = (district?.users && district?.users.filter(filterAdmins)) || [];
      return (
        <OrganizationTile
          key={district.name}
          title={district.name}
          type={OrganizationType.DISTRICT}
          admins={admins.map(getAdmin)}
          schools={schools.length}
          organization={district}
          {...props}
        />
      );
    });
  }, [districts, props]);

  const renderOrgs = (organizations: Array<Organization>): Array<React.ReactElement> => {
    return organizations.map(function DistrictTile(org: Organization) {
      const classroomsCount =
        org.classrooms?.filter(({ dateArchived }) => !dateArchived).length || 0;
      const admins = (org?.users && org?.users.filter(filterAdmins)) || [];
      return (
        <OrganizationTile
          key={org.name}
          title={org.name}
          admins={admins.map(getAdmin)}
          type={org.type as OrganizationTypeMap}
          classrooms={classroomsCount}
          organization={org}
          {...props}
        />
      );
    });
  };
  return (
    <PageContainer loading={loading} error={error}>
      <HeaderBar {...props} />
      <Container className={classes.container}>
        {districts.length > 0 && (
          <Grid container spacing={3} className={classes.grid}>
            {renderDistricts()}
          </Grid>
        )}
        {schools.length > 0 && (
          <Grid
            container
            spacing={3}
            className={cx(classes.grid, { [classes.twoGrids]: twoTypes })}
          >
            {renderOrgs(schools)}
          </Grid>
        )}
        {userGroups.length > 0 && (
          <Grid
            container
            spacing={3}
            className={cx(classes.grid, { [classes.twoGrids]: twoTypes })}
          >
            {renderOrgs(userGroups)}
          </Grid>
        )}
      </Container>
    </PageContainer>
  );
};

export default AdminAdminOrganizations;
